import React, { useState, useEffect, useMemo } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

const ActiveUsersTab = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await adminFetch(
          `${API_URL}/api/admin/active-users`
        );
        const data = await res.json();
        if (data.success) {
          setUsers(data.users);
        } else {
          setError(data.message || 'Failed to load');
        }
      } catch (err) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [user]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = q
      ? users.filter(u =>
          (u.name + ' ' + u.family).toLowerCase().includes(q) ||
          u.username.toLowerCase().includes(q) ||
          (u.community[0] || '').toLowerCase().includes(q)
        )
      : users;

    return [...list].sort((a, b) => {
      const va = a[sortField] || '';
      const vb = b[sortField] || '';
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [users, search, sortField, sortDir]);

  const sortIcon = (field) => {
    if (sortField !== field) return ' \u2195';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ padding: '2rem', color: '#dc2626' }}>{error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
          Active Next Week
          <span style={{
            marginLeft: '0.75rem',
            fontSize: '0.9rem',
            background: '#dbeafe',
            color: '#1d4ed8',
            padding: '2px 10px',
            borderRadius: '12px',
            fontWeight: 600,
          }}>
            {filtered.length}
          </span>
        </h2>
        <input
          type="text"
          placeholder="Search name, username, community..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '0.875rem',
            width: '280px',
          }}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={thStyle}>#</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('name')}>
                Name{sortIcon('name')}
              </th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('username')}>
                Telegram{sortIcon('username')}
              </th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>
                Registered{sortIcon('createdAt')}
              </th>
              <th style={thStyle}>Languages</th>
              <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => handleSort('status')}>
                Status{sortIcon('status')}
              </th>
              <th style={thStyle}>Community</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u, i) => (
              <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={tdStyle}>{i + 1}</td>
                <td style={tdStyle}>{u.name} {u.family}</td>
                <td style={tdStyle}>
                  {u.username ? (
                    <a
                      href={`https://t.me/${u.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#4f46e5', textDecoration: 'none' }}
                    >
                      @{u.username}
                    </a>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>-</span>
                  )}
                </td>
                <td style={tdStyle}>{u.createdAt || '-'}</td>
                <td style={tdStyle}>{u.languages.join(', ') || '-'}</td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    background: statusColor(u.status).bg,
                    color: statusColor(u.status).text,
                  }}>
                    {u.status}
                  </span>
                </td>
                <td style={tdStyle}>{u.community[0] || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
          {search ? 'No matches for search' : 'No active users for next week'}
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '0.5rem 0.75rem', fontWeight: 600, whiteSpace: 'nowrap' };
const tdStyle = { padding: '0.5rem 0.75rem' };

function statusColor(status) {
  switch (status) {
    case 'Admin': return { bg: '#fef3c7', text: '#92400e' };
    case 'PRO': return { bg: '#dbeafe', text: '#1e40af' };
    case 'Premium': return { bg: '#ede9fe', text: '#5b21b6' };
    case 'Free': return { bg: '#f3f4f6', text: '#374151' };
    case 'EarlyBird': return { bg: '#d1fae5', text: '#065f46' };
    default: return { bg: '#f3f4f6', text: '#374151' };
  }
}

export default ActiveUsersTab;
